/**
 * Post-login redirect helpers.
 *
 * When an unauthenticated user hits a protected route, the Protected guard
 * stashes the intended path so AuthCallback can return them there after the
 * OIDC round-trip instead of always dropping them on the dashboard.
 *
 * Both the stash and the read sanitise the value to a same-origin in-app path
 * so a poisoned sessionStorage value can never redirect off-site or back into
 * the auth flow (which would loop).
 */
const KEY = 'post_login_redirect';

/** True if `path` is a safe in-app destination to redirect to after login. */
export function isSafeRedirect(path: string | null | undefined): path is string {
  if (!path) return false;
  // Must be a root-relative path, not a protocol-relative ("//evil.com") or
  // absolute URL, and not a route inside the auth flow itself.
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.startsWith('/login')) return false;
  if (path.startsWith('/auth/')) return false;
  if (path === '/') return false; // dashboard is the fallback anyway
  return true;
}

/** Stash the intended path (no-op if unsafe or storage unavailable). */
export function stashPostLoginRedirect(path: string | null | undefined): void {
  if (!isSafeRedirect(path)) return;
  try { sessionStorage.setItem(KEY, path); } catch { /* ignore */ }
}

/** Read + clear the stashed path, returning a safe destination ('/' fallback). */
export function takePostLoginRedirect(): string {
  try {
    const saved = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isSafeRedirect(saved) ? saved : '/';
  } catch {
    return '/';
  }
}
